import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from '@tanstack/react-router';
import { Badge, Button, EmptyState, Field, Section, Select, Spinner, Switch, Textarea } from '@zt/ui';
import type { Tone } from '@zt/ui';
import { ConfirmButton } from '../components/Confirm';
import { ContentReviewPanel, reviewTone } from '../components/ContentReview';
import { api } from '../lib/api';
import { HelpTip, PageFrame } from '../lib/help';
import { useI18n, useT } from '../lib/i18n';
import { fieldError, useApiMutation } from '../lib/mutation';
import { usePermissions } from '../lib/session';
import type { ReviewDetail, ReviewList } from '../lib/types';
import { StorePicker, useStoreChoice } from './payments';

/**
 * Ürün değerlendirmeleri (F5B-06): yalnızca ürünü teslim almış müşterilerin değerlendirmeleri gelir. Onay
 * bekleyenler, yayındakiler ve reddedilenler; değerlendirme ayrıntısında mağaza yanıtı, onay/ret ve sağlık
 * beyanı varsa uzman (veteriner) kararı.
 */

type Status = 'pending' | 'published' | 'rejected';
const statusTone: Record<Status, Tone> = { pending: 'amber', published: 'moss', rejected: 'slate' };
const stars = (n: number) => `${'★'.repeat(n)}${'☆'.repeat(5 - n)}`;

export function ReviewsPage() {
  const t = useT();
  const { formatDate } = useI18n();
  const { stores, loading, storeId, setStoreId } = useStoreChoice();
  const [status, setStatus] = useState<Status>('pending');
  const [rating, setRating] = useState('');
  const list = useQuery({
    queryKey: ['reviews', storeId, status, rating],
    queryFn: () =>
      api.get<ReviewList>(
        `/v1/admin/reviews?${new URLSearchParams({ storeId, status, ...(rating ? { rating } : {}) })}`,
      ),
    enabled: !!storeId,
  });
  if (loading) return <Spinner label={t('common.loading')} />;
  return (
    <PageFrame helpKey="page.reviews">
      {!stores.length ? (
        <EmptyState title={t('admin.payments.noStore')} description={t('admin.payments.noStoreLead')} />
      ) : (
        <>
          <StorePicker stores={stores} storeId={storeId} onChange={setStoreId} />
          <ReviewsToggle />
          <div className="flex flex-wrap gap-4">
            <Field label={t('admin.reviews.filter')} help={<HelpTip k="reviews.filter" />} className="w-60">
              {(p) => (
                <Select {...p} value={status} onChange={(e) => setStatus(e.target.value as Status)}>
                  {(['pending', 'published', 'rejected'] as const).map((s) => (
                    <option key={s} value={s}>
                      {t(`admin.reviews.statuses.${s}`)}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
            <Field label={t('admin.reviews.ratingFilter')} className="w-48">
              {(p) => (
                <Select {...p} value={rating} onChange={(e) => setRating(e.target.value)}>
                  <option value="">{t('admin.reviews.allRatings')}</option>
                  {[5, 4, 3, 2, 1].map((n) => (
                    <option key={n} value={n}>
                      {t('admin.reviews.ratingN', { n })}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
          </div>
          {!list.data ? (
            <Spinner label={t('common.loading')} />
          ) : !list.data.length ? (
            <EmptyState title={t(`admin.reviews.empty.${status}`)} />
          ) : (
            <ul
              className="divide-y divide-line rounded-md border border-line bg-surface"
              aria-label={t('admin.reviews.listLabel')}
            >
              {list.data.map((r) => (
                <li key={r.id} className="flex flex-wrap items-start gap-x-4 gap-y-2 px-4 py-3 text-sm">
                  <div className="min-w-0 flex-1">
                    <p>
                      <span className="text-amber" aria-hidden>
                        {stars(r.rating)}
                      </span>{' '}
                      <Link
                        to="/urun-degerlendirmeleri/$id"
                        params={{ id: r.id }}
                        className="font-medium text-pine hover:underline"
                      >
                        {t('admin.reviews.itemLabel', { rating: r.rating, title: r.title ?? r.excerpt })}
                      </Link>
                    </p>
                    <p className="text-ink-3">
                      {r.productTitle} · {r.authorName} · {formatDate(r.createdAt, true)}
                    </p>
                  </div>
                  <span className="flex flex-wrap gap-1.5">
                    {r.verifiedPurchase && <Badge tone="moss">{t('admin.reviews.verified')}</Badge>}
                    {r.hasReply && <Badge tone="neutral">{t('admin.reviews.replied')}</Badge>}
                    {r.reviewStatus !== 'not_required' && (
                      <Badge tone={reviewTone[r.reviewStatus]}>
                        {t(`admin.pages.reviews.${r.reviewStatus}`)}
                      </Badge>
                    )}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </PageFrame>
  );
}

/** Ayarlar → Özellikler: değerlendirmeler vitrinde açık mı (kapalıysa bölüm gösterilmez, yazılamaz). */
function ReviewsToggle() {
  const t = useT();
  const { can } = usePermissions();
  const q = useQuery({
    queryKey: ['settings', 'features'],
    queryFn: () => api.get<{ b2bEnabled: boolean; reviewsEnabled: boolean }>('/v1/admin/settings/features'),
  });
  const save = useApiMutation(
    (on: boolean) => api.put('/v1/admin/settings/features', { ...q.data, reviewsEnabled: on }),
    { invalidate: [['settings', 'features']], success: t('common.saved') },
  );
  if (!q.data) return null;
  return (
    <div className="flex items-center gap-3">
      <Switch
        checked={q.data.reviewsEnabled}
        disabled={!can('org.settings.update') || save.isPending}
        onCheckedChange={(v) => save.mutate(v)}
        label={t('admin.reviews.enabled')}
      />
      <span className="text-sm">{t('admin.reviews.enabled')}</span>
      <HelpTip k="reviews.enabled" />
    </div>
  );
}

export function ReviewDetailPage() {
  const t = useT();
  const { formatDate } = useI18n();
  const { id } = useParams({ from: '/app/urun-degerlendirmeleri/$id' });
  const { can } = usePermissions();
  const canModerate = can('catalog.review.moderate');
  const q = useQuery({
    queryKey: ['review', id],
    queryFn: () => api.get<ReviewDetail>(`/v1/admin/reviews/${id}`),
  });
  const item = q.data;
  const [reply, setReply] = useState('');
  const [note, setNote] = useState('');
  useEffect(() => {
    if (item) setReply(item.reply ?? '');
  }, [item]);
  const dirty = !!item && reply.trim() !== (item.reply ?? '');
  const inv = { invalidate: [['review', id], ['reviews']] };
  const save = useApiMutation(
    () => api.put<ReviewDetail>(`/v1/admin/reviews/${id}/reply`, { reply, version: item!.version }),
    { ...inv, success: t('admin.reviews.replySaved') },
  );
  const publish = useApiMutation(
    () => api.post<ReviewDetail>(`/v1/admin/reviews/${id}/publish`, { version: item!.version }),
    { ...inv, success: t('admin.reviews.publishedToast') },
  );
  const reject = useApiMutation(
    () =>
      api.post<ReviewDetail>(`/v1/admin/reviews/${id}/reject`, {
        note: note || null,
        version: item!.version,
      }),
    { ...inv, success: t('admin.reviews.rejectedToast') },
  );
  const expert = useApiMutation(
    (v: { decision: 'approved' | 'rejected'; note: string }) =>
      api.post<ReviewDetail>(`/v1/admin/reviews/${id}/expert-review`, {
        decision: v.decision,
        note: v.note || null,
        contentHash: item!.contentHash,
        version: item!.version,
      }),
    { ...inv, success: t('admin.pages.reviewedToast') },
  );
  if (!item) return <Spinner label={t('common.loading')} />;
  return (
    <PageFrame helpKey="page.review_detail" title={t('admin.reviews.detailTitle')}>
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <Link to="/urun-degerlendirmeleri" className="text-pine hover:underline">
          {t('admin.reviews.back')}
        </Link>
        <Badge tone={statusTone[item.status]}>{t(`admin.reviews.statuses.${item.status}`)}</Badge>
        {item.orderNumber && (
          <Badge tone="moss">{t('admin.reviews.verifiedOrder', { number: item.orderNumber })}</Badge>
        )}
      </div>
      <Section title={t('admin.reviews.review')} className="border-t-0 pt-0">
        <p className="text-lg">
          <span className="text-amber" aria-hidden>
            {stars(item.rating)}
          </span>{' '}
          <span>{t('admin.reviews.ratingOutOf', { n: item.rating })}</span>
        </p>
        {item.title && <h3 className="mt-2 font-semibold">{item.title}</h3>}
        <blockquote className="mt-2 whitespace-pre-line border-s-[3px] border-insert-rule bg-insert p-4 text-[0.9375rem]">
          {item.body}
        </blockquote>
        <p className="mt-2 text-sm text-ink-2">
          {t('admin.reviews.meta', { author: item.authorName, date: formatDate(item.createdAt, true) })}
          {item.product && (
            <>
              {' · '}
              {item.product.title}
            </>
          )}
        </p>
      </Section>
      <Section title={t('admin.reviews.reply')} description={t('admin.reviews.replyLead')}>
        <fieldset disabled={!canModerate} className="grid max-w-3xl gap-3">
          <Field
            label={t('admin.reviews.replyLabel')}
            optionalLabel={t('common.optional')}
            help={<HelpTip k="reviews.reply" />}
            error={fieldError(save.error, 'reply')}
            description={t('admin.pages.chars', { count: reply.length, max: 2000 })}
          >
            {(p) => (
              <Textarea
                {...p}
                rows={4}
                maxLength={2000}
                value={reply}
                onChange={(e) => setReply(e.target.value)}
              />
            )}
          </Field>
          {item.repliedByName && item.repliedAt && (
            <p className="text-sm text-ink-3">
              {t('admin.reviews.repliedBy', {
                name: item.repliedByName,
                date: formatDate(item.repliedAt, true),
              })}
            </p>
          )}
          <div className="flex flex-wrap items-center gap-2">
            <Button onClick={() => save.mutate(undefined)} disabled={!dirty || save.isPending}>
              {t('admin.reviews.saveReply')}
            </Button>
            <HelpTip k="reviews.save_reply" />
            <Button
              variant="primary"
              onClick={() => publish.mutate(undefined)}
              disabled={dirty || publish.isPending || item.status === 'published'}
            >
              {t('admin.reviews.publish')}
            </Button>
            <HelpTip k="reviews.publish" />
            {dirty && <span className="text-sm text-ink-2">{t('admin.reviews.saveFirst')}</span>}
          </div>
          {publish.error && !fieldError(publish.error, 'reply') && (
            <p role="alert" className="text-sm text-rose">
              {publish.error.title}
            </p>
          )}
        </fieldset>
      </Section>
      <ContentReviewPanel
        status={item.reviewStatus}
        note={item.reviewNote}
        findings={item.findings}
        required={item.requiresReview}
        dirty={dirty}
        canReview={can('content.expert_review')}
        busy={expert.isPending}
        onDecide={(decision, n) => expert.mutate({ decision, note: n })}
      />
      {canModerate && item.status !== 'rejected' && (
        <Section title={t('admin.reviews.rejectTitle')} description={t('admin.reviews.rejectLead')}>
          <div className="grid max-w-xl gap-2">
            <Field
              label={t('admin.reviews.rejectNote')}
              optionalLabel={t('common.optional')}
              help={<HelpTip k="reviews.reject" />}
            >
              {(p) => (
                <Textarea
                  {...p}
                  rows={2}
                  maxLength={500}
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                />
              )}
            </Field>
            <div>
              <ConfirmButton
                variant="secondary"
                message={t('admin.reviews.rejectConfirm')}
                onConfirm={() => reject.mutateAsync(undefined)}
              >
                {item.status === 'published' ? t('admin.reviews.unpublish') : t('admin.reviews.reject')}
              </ConfirmButton>
            </div>
          </div>
        </Section>
      )}
      {item.status === 'rejected' && item.moderationNote && (
        <p className="text-sm text-ink-2">{t('admin.reviews.rejectedNote', { note: item.moderationNote })}</p>
      )}
    </PageFrame>
  );
}
